import { skipToken, useQuery } from '@tanstack/react-query';

import { CompanyExportRenderer } from '~hooks/useCompanyExportRenderers/models';
import useCompanyExportRenderers from '~hooks/useCompanyExportRenderers/useCompanyExportRenderers';
import { Nullable } from '~types/Nullable';

// Renderers are enabled by a Platform Admin and change rarely, so the settlements
// page re-mounting should not refetch them every time.
const STALE_TIME_IN_MS = 5 * 60 * 1000;

const EMPTY_COMPANY_EXPORT_RENDERERS: CompanyExportRenderer[] = [];

export const companyExportRenderersQueryKey = (companyId: Nullable<string>) => [
  'companyExportRenderers',
  companyId,
];

interface UseCompanyExportRenderersQueryProps {
  companyId: Nullable<string>;
}

export default function useCompanyExportRenderersQuery({
  companyId,
}: UseCompanyExportRenderersQueryProps) {
  const { fetchAllCompanyExportRenderers } = useCompanyExportRenderers();

  const { data, isError, isPending } = useQuery({
    queryKey: companyExportRenderersQueryKey(companyId),
    queryFn: companyId
      ? async ({ signal }) =>
          (await fetchAllCompanyExportRenderers(companyId, signal)) ?? []
      : skipToken,
    // fetchAllCompanyExportRenderers already toasts through connection.handleRequestError,
    // so React Query's default retries would surface one failure several times.
    retry: false,
    staleTime: STALE_TIME_IN_MS,
  });

  return {
    companyExportRenderers: data ?? EMPTY_COMPANY_EXPORT_RENDERERS,
    isError,
    isPending,
  };
}
